import { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { apiGet, apiPost, apiDelete } from "@/lib/api";
import { deleteWithUndo } from "@/lib/delete-with-undo";
import { toast } from "sonner";
import {
  Plus,
  Copy,
  QrCode,
  Trash2,
  Ban,
  Clock,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { SearchableSelect } from "@/components/ui/searchable-select";

export const Route = createFileRoute("/_authenticated/admin/invitations")({
  component: AdminInvitationsPage,
});

interface Invitation {
  id: string;
  projectId: string;
  projectName: string;
  email: string | null;
  companyName: string | null;
  status: string;
  expiresAt: string;
  accessExpiresAt: string;
  invitedByName: string;
  createdAt: string;
}

interface InvitationDetail extends Invitation {
  token: string;
  acceptedByUserId: string | null;
  acceptedAt: string | null;
  revokedAt: string | null;
  inviteUrl: string;
}

interface Project {
  id: string;
  name: string;
}

const STATUS_CONFIG: Record<string, { label: string; className: string; icon: typeof CheckCircle2 }> = {
  pending: { label: "Pending", className: "bg-amber-100 text-amber-800", icon: Clock },
  accepted: { label: "Accepted", className: "bg-green-100 text-green-800", icon: CheckCircle2 },
  revoked: { label: "Revoked", className: "bg-red-100 text-red-800", icon: Ban },
  expired: { label: "Expired", className: "bg-gray-100 text-gray-600", icon: XCircle },
};

function AdminInvitationsPage() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "invitations", statusFilter, page],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: "20",
      });
      if (statusFilter !== "all") params.set("status", statusFilter);
      return apiGet<{ data: Invitation[]; total: number }>(
        `/api/invitations/manage?${params}`
      );
    },
  });

  const invitations = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / 20);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/invitations/manage/${id}`),
    onSuccess: () => {
      toast.success("Invitation deleted");
      queryClient.invalidateQueries({ queryKey: ["admin", "invitations"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) =>
      apiPost(`/api/invitations/manage/${id}/revoke`, {}),
    onSuccess: () => {
      toast.success("Invitation revoked");
      queryClient.invalidateQueries({ queryKey: ["admin", "invitations"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading && invitations.length === 0) {
    return (
      <div className="space-y-2 py-8">
        <div className="h-4 bg-muted rounded animate-pulse" />
        <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Subcontractor Invitations</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Invite subcontractors and track their onboarding status.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4 mr-1" />
          New Invite
        </Button>
      </div>

      {/* Status filter chips */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs text-muted-foreground mr-1">Status:</span>
        {["all", "pending", "accepted", "revoked", "expired"].map((s) => (
          <button
            key={s}
            onClick={() => {
              setStatusFilter(s);
              setPage(1);
            }}
            className={cn(
              "px-2.5 py-1 rounded-full text-xs font-medium transition-colors capitalize",
              statusFilter === s
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            )}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Invitation list */}
      {invitations.length === 0 ? (
        <div className="flex items-center justify-center h-32 border border-dashed rounded-lg">
          <p className="text-sm text-muted-foreground">
            {statusFilter !== "all"
              ? "No invitations match this filter"
              : "No invitations yet. Create one to onboard a subcontractor."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {invitations.map((inv) => {
            const config = STATUS_CONFIG[inv.status] || STATUS_CONFIG.pending;
            const isExpired =
              inv.status === "pending" && new Date(inv.expiresAt) < new Date();

            return (
              <Card
                key={inv.id}
                className="cursor-pointer transition-colors hover:bg-muted/30"
                onClick={() => setDetailId(inv.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">
                          {inv.projectName}
                        </span>
                        <Badge
                          className={cn(
                            "text-xs px-1.5 py-0 border-0",
                            isExpired
                              ? "bg-gray-100 text-gray-600"
                              : config.className
                          )}
                        >
                          {isExpired ? "Expired" : config.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        {inv.companyName && <span>{inv.companyName}</span>}
                        {inv.email && <span>{inv.email}</span>}
                        <span>
                          by {inv.invitedByName} &middot;{" "}
                          {new Date(inv.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {inv.status === "pending" && !isExpired && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-red-500 hover:text-red-600"
                          title="Revoke"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              window.confirm(
                                "Revoke this invitation? The link will stop working."
                              )
                            ) {
                              revokeMutation.mutate(inv.id);
                            }
                          }}
                        >
                          <Ban className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {inv.status === "pending" && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          title="Delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteWithUndo(inv.email ?? "invitation", () => deleteMutation.mutateAsync(inv.id));
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages} ({total} invitations)
          </span>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create Sheet */}
      <CreateInviteSheet
        open={showCreate}
        onClose={() => setShowCreate(false)}
      />

      {/* Detail Sheet */}
      <InviteDetailSheet
        inviteId={detailId}
        onClose={() => setDetailId(null)}
        onRevoke={(id) => revokeMutation.mutate(id)}
      />
    </div>
  );
}

// --------------- Create Invite Sheet ---------------

function CreateInviteSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [projectId, setProjectId] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [accessDays, setAccessDays] = useState("90");
  const [result, setResult] = useState<{
    inviteUrl: string;
    projectName: string;
  } | null>(null);

  const { data: projectsData } = useQuery({
    queryKey: ["projects-for-invite"],
    queryFn: () =>
      apiGet<{ data: Project[] }>("/api/projects?limit=100"),
    enabled: open,
  });
  const projects = projectsData?.data || [];

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost<{
        data: { id: string; inviteUrl: string; projectName: string };
      }>("/api/invitations/manage", body),
    onSuccess: (res) => {
      setResult({
        inviteUrl: res.data.inviteUrl,
        projectName: res.data.projectName,
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "invitations"] });
      toast.success("Invitation created");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function handleCreate() {
    if (!projectId) return;
    const body: Record<string, unknown> = { projectId };
    if (email.trim()) body.email = email.trim();
    if (companyName.trim()) body.companyName = companyName.trim();
    if (accessDays) body.accessDurationDays = parseInt(accessDays, 10);
    createMutation.mutate(body);
  }

  function handleClose() {
    setProjectId("");
    setEmail("");
    setCompanyName("");
    setAccessDays("90");
    setResult(null);
    onClose();
  }

  function copyUrl() {
    if (result?.inviteUrl) {
      navigator.clipboard.writeText(result.inviteUrl);
      toast.success("Link copied to clipboard");
    }
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && handleClose()}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>New Subcontractor Invite</SheetTitle>
          <SheetDescription>
            Create a project-scoped invitation link for a subcontractor.
          </SheetDescription>
        </SheetHeader>

        {result ? (
          <div className="space-y-4 mt-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-green-800">
                Invite created for {result.projectName}
              </p>
              <p className="text-xs text-green-600 mt-1">
                Share the link below with the subcontractor.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Invite Link</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={result.inviteUrl}
                  className="h-9 text-xs font-mono"
                />
                <Button size="sm" variant="outline" onClick={copyUrl}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Link expires in 7 days. The subcontractor will create an account
                when they open it.
              </p>
            </div>

            <Button size="sm" className="w-full" onClick={handleClose}>
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-4 mt-6">
            <div>
              <Label className="text-xs">Project *</Label>
              <SearchableSelect
                value={projectId}
                onChange={setProjectId}
                allLabel="Select a project"
                allValue=""
                options={projects.map((p) => ({ value: p.id, label: p.name }))}
              />
            </div>
            <div>
              <Label className="text-xs">Email (optional)</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-9 text-sm"
                placeholder="sub@company.com"
              />
              <p className="text-xs text-muted-foreground mt-1">
                If set, only this email can accept the invite.
              </p>
            </div>
            <div>
              <Label className="text-xs">Company Name (optional)</Label>
              <Input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="h-9 text-sm"
                placeholder="ABC Electrical Ltd."
              />
            </div>
            <div>
              <Label className="text-xs">Access Duration (days)</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={accessDays}
                onChange={(e) => setAccessDays(e.target.value)}
                className="h-9 text-sm w-24"
              />
              <p className="text-xs text-muted-foreground mt-1">
                How long the subcontractor can access the project after signing
                up.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button size="sm" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={!projectId || createMutation.isPending}
                onClick={handleCreate}
              >
                {createMutation.isPending ? "Creating..." : "Create Invite"}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// --------------- Invite Detail Sheet ---------------

function InviteDetailSheet({
  inviteId,
  onClose,
  onRevoke,
}: {
  inviteId: string | null;
  onClose: () => void;
  onRevoke: (id: string) => void;
}) {
  const [showQr, setShowQr] = useState(false);
  const [qrSvg, setQrSvg] = useState<string | null>(null);

  // Fetch QR SVG with auth credentials (img tags don't send cookies)
  useEffect(() => {
    if (!showQr || !inviteId) {
      setQrSvg(null);
      return;
    }
    const base = import.meta.env.VITE_API_URL || "";
    fetch(`${base}/api/invitations/manage/${inviteId}/qr`, {
      credentials: "include",
    })
      .then((r) => r.text())
      .then((svg) => setQrSvg(svg))
      .catch(() => setQrSvg(null));
  }, [showQr, inviteId]);

  const { data: detailData } = useQuery({
    queryKey: ["admin", "invitations", inviteId, "detail"],
    queryFn: () =>
      apiGet<{ data: InvitationDetail }>(
        `/api/invitations/manage/${inviteId}`
      ),
    enabled: !!inviteId,
  });

  const detail = detailData?.data;

  function copyUrl() {
    if (detail?.inviteUrl) {
      navigator.clipboard.writeText(detail.inviteUrl);
      toast.success("Link copied to clipboard");
    }
  }

  return (
    <Sheet open={!!inviteId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Invitation Details</SheetTitle>
          <SheetDescription>
            {detail?.projectName || "Loading..."}
          </SheetDescription>
        </SheetHeader>

        {detail && (
          <div className="space-y-4 mt-6">
            {/* Status */}
            <div className="flex items-center gap-2">
              <Badge
                className={cn(
                  "text-xs border-0",
                  (STATUS_CONFIG[detail.status] || STATUS_CONFIG.pending)
                    .className
                )}
              >
                {(STATUS_CONFIG[detail.status] || STATUS_CONFIG.pending).label}
              </Badge>
            </div>

            {/* Info grid */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-xs text-muted-foreground block">
                  Project
                </span>
                <span>{detail.projectName}</span>
              </div>
              {detail.companyName && (
                <div>
                  <span className="text-xs text-muted-foreground block">
                    Company
                  </span>
                  <span>{detail.companyName}</span>
                </div>
              )}
              {detail.email && (
                <div>
                  <span className="text-xs text-muted-foreground block">
                    Email Lock
                  </span>
                  <span className="text-xs">{detail.email}</span>
                </div>
              )}
              <div>
                <span className="text-xs text-muted-foreground block">
                  Invited By
                </span>
                <span>{detail.invitedByName}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">
                  Created
                </span>
                <span>
                  {new Date(detail.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">
                  Link Expires
                </span>
                <span>
                  {new Date(detail.expiresAt).toLocaleDateString()}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">
                  Access Expires
                </span>
                <span>
                  {new Date(detail.accessExpiresAt).toLocaleDateString()}
                </span>
              </div>
              {detail.acceptedAt && (
                <div>
                  <span className="text-xs text-muted-foreground block">
                    Accepted
                  </span>
                  <span>
                    {new Date(detail.acceptedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
              {detail.revokedAt && (
                <div>
                  <span className="text-xs text-muted-foreground block">
                    Revoked
                  </span>
                  <span>
                    {new Date(detail.revokedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>

            {/* Invite Link */}
            {detail.status === "pending" && (
              <div className="space-y-2">
                <Label className="text-xs">Invite Link</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={detail.inviteUrl}
                    className="h-9 text-xs font-mono"
                  />
                  <Button size="sm" variant="outline" onClick={copyUrl}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* QR Code */}
            {detail.status === "pending" && (
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setShowQr(!showQr)}
                >
                  <QrCode className="h-4 w-4 mr-1" />
                  {showQr ? "Hide" : "Show"} QR Code
                  {showQr ? (
                    <ChevronUp className="h-4 w-4 ml-1" />
                  ) : (
                    <ChevronDown className="h-4 w-4 ml-1" />
                  )}
                </Button>
                {showQr && (
                  <div className="mt-3 flex justify-center">
                    {qrSvg ? (
                      <div
                        className="w-48 h-48"
                        dangerouslySetInnerHTML={{ __html: qrSvg }}
                      />
                    ) : (
                      <div className="w-48 h-48 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-paris-red border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            {detail.status === "pending" && (
              <div className="flex gap-2 pt-2">
                <Button
                  size="sm"
                  variant="destructive"
                  className="flex-1"
                  onClick={() => {
                    if (
                      window.confirm(
                        "Revoke this invitation? The link will stop working."
                      )
                    ) {
                      onRevoke(detail.id);
                      onClose();
                    }
                  }}
                >
                  <Ban className="h-4 w-4 mr-1" />
                  Revoke
                </Button>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
